import { type HotelListItem, type HotelListQuery } from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  UzConfirmDialog,
  UzDropdownMenu,
  UzInput,
  UzStatusBadge,
  UzTooltip,
} from '@utazzo/ui';
import { motion } from 'framer-motion';
import {
  Copy,
  FileSpreadsheet,
  FilterX,
  LayoutGrid,
  List as ListIcon,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { HotelCard } from '../components/HotelCard';
import { listHotels } from '../hotels.api';
import {
  useBulkDeleteHotels,
  useDeleteHotel,
  useDuplicateHotel,
  useHotelFacets,
  useHotelsList,
} from '../use-hotels';

import {
  DataGrid,
  DataGridFilterChip,
  DataGridPagination,
  type DataGridColumn,
  type DataGridFilter,
} from '@/components/data-grid';
import { useListScreen } from '@/components/list-screen';
import { downloadExcel, fetchAllPages } from '@/components/list-screen/list-screen-utils';
import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import {
  useCatalogGeoFilters,
  useSelectedCountryId,
  withGeoFilters,
} from '@/hooks/use-catalog-geo-filters';
import { stagger } from '@/lib/motion';

/**
 * Preferred-hotel master list (Phase B4.7 / hotel reskin).
 *
 * Two views over the same data + state:
 *   • Grid (default) - card grid with swatch hero, status pill, vendor footer.
 *     Optimised for browsing/picking when you don't remember the exact name.
 *   • List - DataGrid with the full column set + sort + density. Optimised
 *     for ops + bulk actions.
 *
 * Both modes use the SAME `useListScreen` hook so search, filters, sort, page
 * stay in sync as you toggle. The view toggle lives in the page actions.
 */

// ---------------------------------------------------------------------------
// Filter dropdown options (static; rendered via DataGrid in list mode, via
// inline selects in grid mode).
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

const STAR_OPTIONS = [
  { value: '', label: 'All ratings' },
  { value: '5', label: '5 star' },
  { value: '4', label: '4 star' },
  { value: '3', label: '3 star' },
  { value: '2', label: '2 star' },
  { value: '1', label: '1 star' },
] as const;

const APPROVAL_OPTIONS = [
  { value: '', label: 'All approvals' },
  { value: 'approved', label: 'Approved only' },
  { value: 'pending', label: 'Not approved' },
] as const;

const TBO_OPTIONS = [
  { value: '', label: 'TBO: all' },
  { value: 'linked', label: 'TBO: linked' },
] as const;

const ARCHIVED_OPTIONS = [
  { value: '', label: 'Active only' },
  { value: 'archived', label: 'Include archived' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'stars',
    label: 'Stars',
    options: STAR_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-36',
  },
  {
    id: 'approval',
    label: 'Approval',
    options: APPROVAL_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
  {
    id: 'tbo',
    label: 'TBO link',
    options: TBO_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
  {
    id: 'archived',
    label: 'Archived',
    options: ARCHIVED_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
];

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const renderStarsAscii = (rating: number): string =>
  rating <= 0 ? '-' : '★'.repeat(rating) + '☆'.repeat(Math.max(0, 5 - rating));

const mapStatus = (s: HotelListItem['status']): 'active' | 'inactive' | 'draft' | 'pending' => {
  switch (s) {
    case 'ACTIVE':
      return 'active';
    case 'DRAFT':
      return 'draft';
    case 'PENDING_APPROVAL':
      return 'pending';
    default:
      return 'inactive';
  }
};

// ---------------------------------------------------------------------------
// View toggle
// ---------------------------------------------------------------------------

type View = 'grid' | 'list';

interface ViewToggleProps {
  view: View;
  onChange: (next: View) => void;
}

const ViewToggle = ({ view, onChange }: ViewToggleProps): React.JSX.Element => {
  const btn = (target: View, Icon: typeof LayoutGrid, label: string): React.JSX.Element => (
    <button
      type="button"
      role="tab"
      aria-selected={view === target}
      onClick={() => onChange(target)}
      className={
        'relative inline-flex h-7 items-center gap-1.5 rounded-[5px] px-2.5 text-xs font-medium transition-colors duration-150 ' +
        (view === target
          ? 'bg-white text-zinc-900 shadow-[0_1px_2px_rgba(0,0,0,0.04)] ring-1 ring-zinc-200'
          : 'text-zinc-500 hover:text-zinc-800')
      }
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
    </button>
  );

  return (
    <div role="tablist" aria-label="View mode" className="inline-flex rounded-md bg-zinc-100 p-0.5">
      {btn('grid', LayoutGrid, 'Grid')}
      {btn('list', ListIcon, 'List')}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Grid-mode toolbar (search + filter dropdowns + clear). List mode uses
// DataGrid's built-in toolbar so we don't render this above it.
// ---------------------------------------------------------------------------

interface GridToolbarProps {
  /** Built by the page - the geo chips' options depend on grid state. */
  filters: ReadonlyArray<DataGridFilter>;
  search: string;
  onSearchChange: (q: string) => void;
  filterState: Record<string, string>;
  onFilterChange: (id: string, value: string) => void;
  resultCount: number;
}

const GridToolbar = ({
  filters,
  search,
  onSearchChange,
  filterState,
  onFilterChange,
  resultCount,
}: GridToolbarProps): React.JSX.Element => {
  const activeFilters = filters.filter((f) => Boolean(filterState[f.id]));
  return (
    <div className="mb-4 flex flex-col gap-2">
      {/* Two bands, mirroring DataGridToolbar: the list's own controls up top,
          the filter chips on their own full-width row underneath so a long
          chip set reads as one line. */}
      <div className="flex flex-wrap items-center gap-2">
        <UzInput
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search hotels..."
          leftAdornment={<Search className="h-3.5 w-3.5" aria-hidden />}
          containerClassName="w-64"
        />
        <span className="text-[13px] whitespace-nowrap text-zinc-500 tabular-nums">
          Showing <span className="font-medium text-zinc-700">{resultCount}</span>
          {' results'}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {filters.map((f) => (
          <DataGridFilterChip
            key={f.id}
            filter={f}
            value={filterState[f.id] ?? ''}
            onChange={(v) => onFilterChange(f.id, v)}
          />
        ))}
        {activeFilters.length > 0 && (
          <button
            type="button"
            title="Clear filters"
            onClick={() => filters.forEach((f) => onFilterChange(f.id, ''))}
            className="focus-visible:ring-ring inline-flex h-[34px] cursor-pointer items-center gap-1 rounded-md px-1.5 text-[13px] text-[var(--fg-muted)] transition-colors hover:text-[var(--fg)] focus-visible:ring-2 focus-visible:outline-none"
          >
            <FilterX className="h-3.5 w-3.5" aria-hidden />
            Clear · {activeFilters.length}
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const HotelsListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteHotel = useDeleteHotel();
  const bulkDeleteMut = useBulkDeleteHotels();
  const duplicateHotel = useDuplicateHotel();
  const navigate = useNavigate();
  const [view, setView] = useState<View>('list');

  // City options narrow to the selected Country (read from the URL, so the
  // filters are ready before the grid state is built).
  const facets = useHotelFacets();
  const { countryFilter, cityFilter } = useCatalogGeoFilters(facets.data, useSelectedCountryId());
  const filters = useMemo<ReadonlyArray<DataGridFilter>>(
    () => withGeoFilters(FILTERS, countryFilter, cityFilter, 'status'),
    [countryFilter, cityFilter],
  );

  const screen = useListScreen<HotelListItem, HotelListQuery>({
    title: 'Hotels',
    useList: useHotelsList,
    defaultSort: { by: 'createdAt', dir: 'desc' },
    minSearchLength: MIN_SEARCH_LENGTH,
    filters,
    getRowId: (row) => row.id,
    columns: [], // unused; DataGrid columns are passed inline below
    toQuery: (state, base) => {
      const status = state.filters.status;
      const stars = state.filters.stars;
      const approval = state.filters.approval;
      const tbo = state.filters.tbo;
      const archived = state.filters.archived;
      const countryId = state.filters.countryId;
      const cityId = state.filters.cityId;
      return {
        ...base,
        ...(status ? { status: status as HotelListQuery['status'] } : {}),
        ...(stars ? { starRating: Number(stars) } : {}),
        ...(countryId ? { countryId } : {}),
        ...(cityId ? { cityId } : {}),
        ...(approval === 'approved' ? { isApprovedByAdmin: true } : {}),
        ...(approval === 'pending' ? { isApprovedByAdmin: false } : {}),
        ...(tbo === 'linked' ? { hasTboMirror: true } : {}),
        ...(archived === 'archived' ? { includeDeleted: true } : {}),
      };
    },
  });

  const { state, gridState, query, list, data, pagination, deletion } = screen;

  // Inline columns for list mode. Same shape as before but compact (the grid
  // mode is the new identity-bearing surface).
  const listColumns: ReadonlyArray<DataGridColumn<HotelListItem>> = [
    {
      id: 'serial',
      header: 'S.No',
      width: '64px',
      cell: (_row, index) => (
        <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
      ),
    },
    {
      id: 'name',
      header: 'Hotel',
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="font-medium text-zinc-900">{row.name}</span>
            {row.tboHotelCode && (
              <UzTooltip label={`TBO: ${row.tboHotelCode}`}>
                <span>
                  <UzBadge tone="secondary">TBO</UzBadge>
                </span>
              </UzTooltip>
            )}
          </div>
          {row.hotelChain && <span className="text-xs text-zinc-500">{row.hotelChain}</span>}
        </div>
      ),
    },
    {
      id: 'location',
      header: 'Location',
      cell: (row) => (
        <div className="flex flex-col text-sm">
          <span className="text-zinc-900">
            {row.cityName ?? <span className="text-zinc-400">-</span>}
          </span>
          <span className="text-xs text-zinc-500">
            {row.countryName ?? <span className="text-zinc-400">-</span>}
          </span>
        </div>
      ),
    },
    {
      id: 'stars',
      header: 'Stars',
      sortable: true,
      width: '120px',
      cell: (row) => (
        <span className="font-mono text-amber-500">{renderStarsAscii(row.starRating)}</span>
      ),
    },
    {
      id: 'rooms',
      header: 'Rooms',
      width: '90px',
      cell: (row) => (
        <UzBadge tone={row.roomCount > 0 ? 'secondary' : 'default'}>
          {row.roomCount} room{row.roomCount === 1 ? '' : 's'}
        </UzBadge>
      ),
    },
    {
      id: 'vendor',
      header: 'Vendor',
      cell: (row) => (
        <span className="text-zinc-600">
          {row.vendorSupplierName ?? <span className="text-zinc-400">-</span>}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '140px',
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <UzStatusBadge status={mapStatus(row.status)} />
          {row.isApprovedByAdmin && (
            <span className="text-[10px] tracking-wide text-emerald-600 uppercase">Approved</span>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: '52px',
      align: 'center',
      truncate: false,
      resizable: false,
      alwaysVisible: true,
      cell: (row) => (
        <div
          className="flex justify-center"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <UzDropdownMenu
            align="end"
            trigger={
              <UzButton variant="ghost" size="icon" aria-label="Row actions">
                <MoreHorizontal className="h-4 w-4" />
              </UzButton>
            }
            items={[
              {
                label: 'Duplicate to draft',
                icon: <Copy className="h-4 w-4" />,
                disabled: duplicateHotel.isPending,
                onSelect: () => void duplicateHotel.mutateAsync(row.id),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const onOpenHotel = (row: HotelListItem): void => {
    navigate(`/hotels/${row.id}/edit`);
  };

  return (
    <PageShell
      title="Hotels"
      description="Preferred-hotel catalog."
      actions={
        // `contents` keeps the buttons laid out by the header exactly as before;
        // on phones this becomes a real row pinned to the <main> content width
        // (viewport minus its px-4) so it wraps instead of running off-screen.
        <div className="contents max-sm:flex max-sm:w-[calc(100vw-2rem)] max-sm:flex-wrap max-sm:items-center max-sm:gap-2">
          <ViewToggle view={view} onChange={setView} />
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => void list.refetch()}
            loading={list.isFetching}
            aria-label="Refresh"
          >
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Refresh
          </UzButton>
          <UzButton variant="outline" size="sm" onClick={() => navigate('/hotels/imports')}>
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />
            Bulk import
          </UzButton>
          <UzButton onClick={() => navigate('/hotels/new')}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add hotel
          </UzButton>
        </div>
      }
    >
      {view === 'list' ? (
        <DataGrid<HotelListItem>
          columns={listColumns}
          data={data}
          pagination={pagination}
          isLoading={list.isLoading}
          isError={list.isError}
          error={{
            title: 'Failed to load hotels',
            description: 'The server returned an error. Try refreshing or check your network.',
          }}
          onRetry={() => void list.refetch()}
          state={state}
          onStateChange={{
            setSearch: gridState.setSearch,
            setFilter: gridState.setFilter,
            setSort: gridState.setSort,
            setPage: gridState.setPage,
            setPageSize: gridState.setPageSize,
            setDensity: gridState.setDensity,
            setHidden: gridState.setHidden,
          }}
          filters={filters}
          getRowId={(row) => row.id}
          onRowClick={onOpenHotel}
          searchPlaceholder="Search by name, chain, or address (min 3 characters)"
          emptyTitle="No hotels match these filters"
          emptyDescription="Adjust filters or clear the search box."
          selectable
          bulkActions={[
            {
              id: 'bulk-delete',
              label: 'Delete',
              danger: true,
              onAction: (_ids, rows) => {
                void bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
              },
            },
          ]}
          onExport={async () => {
            const rows = await fetchAllPages(listHotels, query);
            downloadExcel(
              rows,
              [
                { header: 'Name', value: (r) => r.name },
                { header: 'City', value: (r) => r.cityName ?? '' },
                { header: 'Country', value: (r) => r.countryName ?? '' },
                { header: 'Stars', value: (r) => r.starRating },
                { header: 'Rooms', value: (r) => r.roomCount },
                { header: 'Vendor', value: (r) => r.vendorSupplierName ?? '' },
                { header: 'Approved', value: (r) => (r.isApprovedByAdmin ? 'Yes' : 'No') },
                { header: 'Status', value: (r) => r.status },
              ],
              'hotels',
            );
          }}
        />
      ) : (
        <>
          <GridToolbar
            filters={filters}
            search={state.q}
            onSearchChange={gridState.setSearch}
            filterState={state.filters}
            onFilterChange={gridState.setFilter}
            resultCount={pagination?.totalItems ?? 0}
          />

          {list.isLoading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[228px] animate-pulse rounded-lg border border-zinc-200 bg-zinc-50/60"
                />
              ))}
            </div>
          ) : list.isError ? (
            <div className="rounded-lg border border-zinc-200 bg-white p-12 text-center">
              <h3 className="text-sm font-semibold text-zinc-900">Failed to load hotels</h3>
              <p className="mx-auto mt-1 max-w-sm text-xs text-zinc-500">
                The server returned an error. Check your connection and try again.
              </p>
              <UzButton
                size="sm"
                variant="outline"
                onClick={() => void list.refetch()}
                className="mt-4"
              >
                Retry
              </UzButton>
            </div>
          ) : data.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-12 text-center">
              <h3 className="text-sm font-semibold text-zinc-900">No hotels match</h3>
              <p className="mx-auto mt-1 max-w-sm text-xs text-zinc-500">
                Adjust filters or clear the search box.
              </p>
            </div>
          ) : (
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {data.map((hotel) => (
                <HotelCard key={hotel.id} hotel={hotel} onClick={onOpenHotel} />
              ))}
            </motion.div>
          )}

          {pagination && data.length > 0 && (
            <div className="mt-4 rounded-md border border-zinc-200 bg-white">
              <DataGridPagination
                page={pagination.page}
                pageSize={pagination.pageSize}
                totalItems={pagination.totalItems}
                onPageChange={gridState.setPage}
                onPageSizeChange={gridState.setPageSize}
              />
            </div>
          )}
        </>
      )}

      {canDelete && (
        <UzConfirmDialog
          open={deletion.target !== null}
          onOpenChange={(open) => {
            if (!open) deletion.clear();
          }}
          title="Archive this hotel?"
          description={
            deletion.target
              ? `${deletion.target.name} will be soft-deleted, status set to ARCHIVED, and hidden from lists. Itinerary components using it stay intact.`
              : ''
          }
          confirmLabel="Archive"
          tone="destructive"
          onConfirm={async () => {
            if (deletion.target) {
              await deleteHotel.mutateAsync(deletion.target.id);
              deletion.clear();
            }
          }}
        />
      )}
    </PageShell>
  );
};
